import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { auth, functions } from "../../firebase";
import { useAccess } from "../auth/AccessProvider";
import ApplicationFormLoader from "./ApplicationFormLoader";
import ApplicationStatus, { isLearnerApplication } from "./ApplicationStatus";
import AccessAlert from "./AccessAlert";
import AccessShell from "./AccessShell";
import CountrySelect from "./CountrySelect";
import PhoneField from "./PhoneField";
import LineSelect from "./LineSelect";
import "./ApplicantIntakePage.css";

const APPLICANT_LEARNER_ROUTE = "/unity/learner";

const CLOSED = new Set(["archived", "inactive", "suspended", "completed", "closed", "ended", "full", "cancelled", "canceled", "draft"]);
const AVAILABLE = new Set(["active", "upcoming", "open"]);
const MONTHS = {
  january: 0, february: 1, march: 2, april: 3, may: 4, june: 5,
  july: 6, august: 7, september: 8, october: 9, november: 10, december: 11,
};
const GENDERS = [
  "Female",
  "Male",
  "Non-binary",
  "Prefer not to say",
  "Other",
];
const QUALIFICATIONS = [
  "None",
  "Completed short courses",
  "High school",
  "Diploma or Certificate (NQF 5 or higher)",
  "Degree",
  "Post graduate qualifications",
];
const EMPLOYMENT = [
  "High-school / College student",
  "University student",
  "Unemployed",
  "Employed – Full-time",
  "Employed – Part-time / Contract",
  "Self-employed / Entrepreneur",
];
const UNDERSTANDING = [
  "I am completely new to the field",
  "I have some understanding about the subject matter but it is limited",
  "I feel confident about my base understanding but I want to upskill myself",
  "I am not sure if this is the correct field for me",
];
const REASONS = [
  "Exploring to see if I will be interested in the subject matter",
  "I want to upskill myself for my current career",
  "I want to change careers",
  "I want to start my own business",
];

function parseIntakeDate(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : new Date(value.getTime());
  if (typeof value === "object") {
    const seconds = value.seconds ?? value._seconds;
    return Number.isFinite(seconds) ? new Date(seconds * 1000) : null;
  }
  const text = String(value).trim();
  if (!text || /^no\b/i.test(text) || text === "Invalid Date") return null;
  const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  const dayFirst = text.match(/^(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})/);
  const monthFirst = text.match(/^([A-Za-z]+)\s+(\d{1,2}),?\s+(\d{4})/);
  const parts = dayFirst
    ? [dayFirst[3], MONTHS[dayFirst[2].toLowerCase()], dayFirst[1]]
    : monthFirst
      ? [monthFirst[3], MONTHS[monthFirst[1].toLowerCase()], monthFirst[2]]
      : null;
  if (parts && parts[1] !== undefined) return new Date(Number(parts[0]), parts[1], Number(parts[2]));
  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatIntakeDate(value) {
  const date = parseIntakeDate(value);
  if (!date) return "";
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

function isOpenIntake(block) {
  const status = String(block.status || "").trim().toLowerCase();
  if (CLOSED.has(status)) return false;
  if (status && !AVAILABLE.has(status)) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const startDate = parseIntakeDate(block.startDate);
  const endDate = parseIntakeDate(block.endDate);
  if (!startDate) return false;
  startDate.setHours(0, 0, 0, 0);
  if (endDate) endDate.setHours(0, 0, 0, 0);
  if (endDate && endDate < today) return false;
  return startDate >= today;
}

function programmeKey(block) {
  return String(block.programme || block.program || block.programId || "").trim();
}

function readableName(value) {
  return String(value || "")
    .replace(/[^a-z0-9]+/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function companyTag(block) {
  return readableName(block.company);
}

function programmeChoice(block) {
  return `${programmeKey(block)}|||${String(block.company || "").trim()}`;
}

function chosenProgramme(choice) {
  return String(choice || "").split("|||")[0];
}

function programmeLabel(block) {
  const tag = companyTag(block);
  let name = readableName(programmeKey(block));
  if (tag) {
    const ending = tag.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    name = name.replace(new RegExp(`\\s+${ending}$`, "i"), "").trim();
  }
  return tag ? `${name} (${tag})` : name;
}

function studyTypeKey(block) {
  const campus = String(block.campus || "").trim().toLowerCase();
  if (!campus || campus === "n/a") return "";
  return campus.includes("online") ? "online" : "onsite";
}

function studyTypeLabel(value) {
  if (value === "online") return "Online";
  if (value === "onsite") return "Onsite";
  return value;
}

function modeKey(block) {
  return String(block.programType || "").trim().toLowerCase();
}

function modeLabel(value) {
  const text = String(value || "").trim().toLowerCase();
  if (text === "full-time") return "Full-time";
  if (text === "part-time") return "Part-time";
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : "Study mode";
}

function dateLabel(block) {
  const start = formatIntakeDate(block.startDate);
  const end = formatIntakeDate(block.endDate);
  if (start && end) return `${start} to ${end}`;
  return start || "Dates to be confirmed";
}

function genderFromId(idNumber) {
  const digits = String(idNumber || "").replace(/\D/g, "");
  if (digits.length < 10) return "";
  const sequence = Number(digits.slice(6, 10));
  if (!Number.isFinite(sequence)) return "";
  return sequence < 5000 ? "Female" : "Male";
}

function ageFromId(idNumber) {
  const digits = String(idNumber || "").replace(/\D/g, "");
  if (digits.length !== 13) return "";
  const yearPart = Number(digits.slice(0, 2));
  const month = Number(digits.slice(2, 4)) - 1;
  const day = Number(digits.slice(4, 6));
  const now = new Date();
  const century = yearPart > now.getFullYear() % 100 ? 1900 : 2000;
  const birth = new Date(century + yearPart, month, day);
  if (Number.isNaN(birth.getTime())) return "";
  let age = now.getFullYear() - birth.getFullYear();
  const birthday = new Date(now.getFullYear(), birth.getMonth(), birth.getDate());
  if (now < birthday) age -= 1;
  return age > 0 && age < 120 ? String(age) : "";
}

function unique(values) {
  return Array.from(new Set(values.filter(Boolean)));
}

async function readApplication(email, company, collectionBrand) {
  const readDoc = httpsCallable(functions, "firestoreReadDocSafe");
  const result = await readDoc({
    company,
    collectionBrand,
    systemName: "applications",
    uniqueId: email,
  });
  if (!result.data?.found || !result.data.data) return null;
  return { company, collectionBrand, ...result.data.data };
}

async function findSubmittedApplication(email, intakes, seen = new Set()) {
  const targets = [];
  intakes.forEach((block) => {
    const company = String(block.company || "").trim();
    const collectionBrand = String(block.collectionBrand || "").trim();
    const key = `${company}/${collectionBrand}`;
    if (!company || !collectionBrand || seen.has(key)) return;
    seen.add(key);
    targets.push({ company, collectionBrand });
  });
  for (const target of targets) {
    const application = await readApplication(email, target.company, target.collectionBrand);
    if (application) return application;
  }
  return null;
}

async function openApplicantMembership(application) {
  const company = String(application?.company || "").trim();
  const collectionBrand = String(application?.collectionBrand || "").trim();
  const intakeId = String(application?.selectedIntakeBlockId || "").trim();
  if (!company || !collectionBrand || !intakeId) {
    throw new Error("This intake does not record which company it belongs to.");
  }
  const openMembership = httpsCallable(functions, "openApplicantMembership");
  await openMembership({ company, collectionBrand, intakeId });
}

export default function ApplicantIntakePage() {
  const { refreshAccessContext, user } = useAccess();
  const [intakes, setIntakes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [countriesSettled, setCountriesSettled] = useState(false);
  const [codesSettled, setCodesSettled] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [phoneReady, setPhoneReady] = useState(false);
  const [showMissing, setShowMissing] = useState(false);
  const [programme, setProgramme] = useState("");
  const [studyType, setStudyType] = useState("");
  const [studyMode, setStudyMode] = useState("");
  const [intakeId, setIntakeId] = useState("");
  const [application, setApplication] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    phone: "",
    country: "",
    idNumber: "",
    gender: "",
    qualificationLevel: "",
    employmentStatus: "",
    subjectUnderstanding: "",
    reason: "",
  });

  useEffect(() => {
    let active = true;
    const load = async () => {
      if (!auth.currentUser) {
        if (active) {
          setIntakes([]);
          setLoading(false);
        }
        return;
      }
      setLoading(true);
      setError("");
      try {
        const email = auth.currentUser?.email || "";
        const listedCompany = "TPC_Group";
        const intakeBrands = ["shared", "TechPerformanceCentre"];
        const getSystemDocuments = httpsCallable(functions, "getSystemDocuments");
        const intakesPromise = Promise.all(intakeBrands.map((collectionBrand) => getSystemDocuments({
          company: listedCompany,
          collectionBrand,
          systemName: "intakeBlocks",
          auth: true,
        }).then((result) => ({ collectionBrand, result })).catch(() => ({ collectionBrand, result: null }))));
        const known = email ? await readApplication(email, listedCompany, intakeBrands[0]) ||
          await readApplication(email, listedCompany, intakeBrands[1]) : null;
        if (!active) return;
        if (known && !isLearnerApplication(known)) {
          try {
            await openApplicantMembership(known);
            await refreshAccessContext({ forceTokenRefresh: true });
            if (active) setApplication(known);
            return;
          } catch (membershipError) {
            if (active) {
              setError(membershipError?.message || "Company access could not be opened.");
            }
          }
        }
        const listed = await intakesPromise;
        const rows = listed.flatMap(({ collectionBrand, result }) => {
          const data = Array.isArray(result?.data?.data) ? result.data.data : [];
          return data.map((row) => ({
            ...row,
            company: row?.company || listedCompany,
            collectionBrand: row?.collectionBrand || collectionBrand,
          }));
        });
        const openIntakes = rows.filter((row) => row && row.uid !== "global_calendar_forbidden_days" && isOpenIntake(row));
        const seen = new Set(intakeBrands.map((brand) => `${listedCompany}/${brand}`));
        const submitted = known && !isLearnerApplication(known) ? null : known || (email ? await findSubmittedApplication(email, openIntakes, seen) : null);
        if (submitted && !isLearnerApplication(submitted)) {
          await openApplicantMembership(submitted);
          await refreshAccessContext({ forceTokenRefresh: true });
        }
        if (active) {
          setIntakes(openIntakes);
          setApplication(submitted);
        }
      } catch (nextError) {
        if (active) setError(nextError?.message || "The intake list could not be loaded.");
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [refreshAccessContext, user]);

  const programmes = useMemo(() => {
    const seen = new Set();
    return intakes.flatMap((block) => {
      const value = programmeChoice(block);
      if (!programmeKey(block) || seen.has(value)) return [];
      seen.add(value);
      return [{ value, label: programmeLabel(block) }];
    });
  }, [intakes]);

  const studyTypes = useMemo(() => {
    const found = new Set(
      intakes
        .filter((block) => !programme || programmeChoice(block) === programme)
        .filter((block) => !studyMode || modeKey(block) === studyMode)
        .map(studyTypeKey)
        .filter(Boolean),
    );
    return ["online", "onsite"].filter((type) => found.has(type));
  }, [intakes, programme, studyMode]);

  const studyModes = useMemo(() => unique(
    intakes
      .filter((block) => !programme || programmeChoice(block) === programme)
      .filter((block) => !studyType || studyTypeKey(block) === studyType)
      .map(modeKey),
  ), [intakes, programme, studyType]);

  const matchingIntakes = useMemo(() => {
    if (!programme || !studyType || !studyMode) return [];
    const seen = new Set();
    return intakes.filter((block) => {
      if (programmeChoice(block) !== programme || studyTypeKey(block) !== studyType || modeKey(block) !== studyMode) return false;
      const key = `${dateLabel(block)}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [intakes, programme, studyMode, studyType]);

  const missing = useMemo(() => {
    const idDigits = form.idNumber.replace(/\D/g, "");
    const items = [];
    if (!programme) items.push("Programme");
    if (!studyType) items.push("Study type");
    if (!studyMode) items.push("Study mode");
    if (!intakeId) items.push("Intake");
    if (!phoneReady) items.push("Phone number");
    if (!form.country.trim()) items.push("Country");
    if (idDigits.length !== 13 || !ageFromId(form.idNumber)) items.push("ID number");
    if (!form.gender) items.push("Gender");
    if (!form.qualificationLevel) items.push("Highest qualification");
    if (!form.employmentStatus) items.push("Employment");
    if (!form.subjectUnderstanding) items.push("Subject understanding");
    if (!form.reason) items.push("Reason for applying");
    return items;
  }, [form, intakeId, phoneReady, programme, studyMode, studyType]);

  const missingSet = useMemo(() => new Set(showMissing ? missing : []), [missing, showMissing]);
  const complete = missing.length === 0;
  const fieldClass = (label, extra = "") => `pa-field${extra ? ` ${extra}` : ""}${missingSet.has(label) ? " is-missing" : ""}`;

  const setField = (name) => (event) => {
    const value = event.target.value;
    setForm((current) => {
      const next = { ...current, [name]: value };
      if (name !== "idNumber") return next;
      const digits = value.replace(/\D/g, "");
      const gender = genderFromId(digits);
      if (digits.length >= 6) next.country = "South Africa";
      if (gender) next.gender = gender;
      return next;
    });
  };

  const submit = async (event) => {
    event.preventDefault();
    const email = user?.email || auth.currentUser?.email || "";
    const age = ageFromId(form.idNumber);
    if (!complete) {
      setShowMissing(true);
      setError("");
      setMessage("");
      return;
    }
    const selectedIntake = intakes.find((block) => String(block.uid || block.id) === intakeId);
    const company = String(selectedIntake?.company || "").trim();
    const collectionBrand = String(selectedIntake?.collectionBrand || "").trim();
    if (!company || !collectionBrand) {
      setError("This intake does not record which company it belongs to.");
      return;
    }
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const firestoreOperations = httpsCallable(functions, "firestoreOperations");
      await firestoreOperations({
        operation: "write",
        company,
        collectionBrand,
        systemName: "applications",
        uniqueId: email,
        auth: true,
        documentData: {
          email,
          uid: auth.currentUser?.uid || "",
          fullName: user?.displayName || auth.currentUser?.displayName || "",
          phone: form.phone.trim(),
          country: form.country.trim(),
          idNumber: form.idNumber.replace(/\D/g, ""),
          gender: form.gender,
          qualificationLevel: form.qualificationLevel,
          employmentStatus: form.employmentStatus,
          subjectUnderstanding: form.subjectUnderstanding,
          reason: form.reason,
          programme: chosenProgramme(programme),
          studyType,
          campus: selectedIntake?.campus || studyType,
          studyMode,
          programType: studyMode,
          selectedIntakeBlockId: intakeId,
          company,
          collectionBrand,
          state: "Applicant",
          isCompleted: true,
          submittedAt: new Date().toISOString(),
        },
      });
      const saved = {
        email,
        uid: auth.currentUser?.uid || "",
        company,
        collectionBrand,
        state: "Applicant",
        programme: chosenProgramme(programme),
        selectedIntakeBlockId: intakeId,
      };
      await openApplicantMembership(saved);
      await refreshAccessContext({ forceTokenRefresh: true });
      setApplication(saved);
      setSubmitted(true);
      setMessage("");
    } catch (nextError) {
      setError(nextError?.message || "The application could not be saved.");
    } finally {
      setBusy(false);
    }
  };

  const markCountries = useCallback((status) => setCountriesSettled(status !== "loading"), []);
  const markCodes = useCallback((status) => setCodesSettled(status !== "loading"), []);
  const email = user?.email || auth.currentUser?.email || "";
  if (email && loading) {
    return (
      <div className="applicant-intake-gate">
        <ApplicationFormLoader />
      </div>
    );
  }
  if (application && !isLearnerApplication(application) && !submitted) {
    return <Navigate to={APPLICANT_LEARNER_ROUTE} replace />;
  }
  const noIntakes = Boolean(email) && !loading && !error && intakes.length === 0;
  const preparing = !noIntakes && (loading || !countriesSettled || !codesSettled);

  return (
    <AccessShell
      eyebrow="Apply"
      title="Your application."
      description={application ? (isLearnerApplication(application) ? "Your learner access is ready." : "The programme stays closed until your application is approved.") : noIntakes ? "There is no active intake block to apply for right now." : "Choose how you want to study. The intake list follows that choice."}
      footer={email ? <p>Signed in as {email}</p> : null}
    >
      {submitted ? (
        <div className="applicant-intake-modal" role="dialog" aria-modal="true" aria-labelledby="applicant-intake-modal-title">
          <div className="applicant-intake-modal__panel">
            <span className="applicant-intake-modal__icon" aria-hidden="true">&#10003;</span>
            <h2 id="applicant-intake-modal-title">Application submitted</h2>
            <p>Your application has been successfully submitted. We have sent a confirmation to {email}.</p>
            <button className="pa-primary-button" type="button" autoFocus onClick={() => setSubmitted(false)}>
              Go to my dashboard
            </button>
          </div>
        </div>
      ) : null}
      <form className="applicant-intake-form pa-form" onSubmit={submit}>
        {preparing ? <ApplicationFormLoader /> : null}
        {!preparing && application ? <ApplicationStatus application={application} /> : null}
        <div className="applicant-intake-form__body" hidden={preparing || Boolean(application)}>
        <AccessAlert tone="error">{error}</AccessAlert>
        <AccessAlert tone="success">{message}</AccessAlert>
        {!email ? <p className="applicant-intake-form__status">Sign in to complete your application.</p> : null}
        {noIntakes ? null : (
        <>
        <label className={fieldClass("Programme")}>
          <span>Programme</span>
          <LineSelect
            value={programme}
            placeholder="Select a programme"
            options={programmes}
            onChange={(next) => {
              setProgramme(next);
              setStudyType("");
              setStudyMode("");
              setIntakeId("");
            }}
          />
        </label>

        <div className="pa-field-grid">
          <label className={fieldClass("Study type")}>
            <span>Study type</span>
            <LineSelect
              value={studyType}
              placeholder={programme ? "Select study type" : "Choose a programme first"}
              disabled={!programme}
              options={studyTypes.map((type) => ({ value: type, label: studyTypeLabel(type) }))}
              onChange={(next) => {
                setStudyType(next);
                setStudyMode("");
                setIntakeId("");
              }}
            />
          </label>
          <label className={fieldClass("Study mode")}>
            <span>Study mode</span>
            <LineSelect
              value={studyMode}
              placeholder="Select study mode"
              disabled={!programme}
              options={studyModes.map((mode) => ({ value: mode, label: modeLabel(mode) }))}
              onChange={(next) => {
                setStudyMode(next);
                setIntakeId("");
              }}
            />
          </label>
        </div>

        <label className={fieldClass("Intake")}>
          <span>Intake</span>
          <LineSelect
            value={intakeId}
            placeholder={programme && studyType && studyMode ? "Select an intake" : "Choose programme, type, and mode first"}
            disabled={!programme || !studyType || !studyMode}
            options={matchingIntakes.map((block) => ({
              value: String(block.uid || block.id),
              label: dateLabel(block),
            }))}
            onChange={setIntakeId}
          />
        </label>

        <div className="pa-field-grid">
          <label className={fieldClass("Phone number", "phone-field-slot")}>
            <span>Phone</span>
            <PhoneField
              onReady={markCodes}
              onChange={(next, detail) => {
                setForm((current) => ({ ...current, phone: next }));
                setPhoneReady(Boolean(detail?.valid));
              }}
            />
          </label>
          <label className={fieldClass("Country")}>
            <span>Country</span>
            <CountrySelect
              value={form.country}
              onReady={markCountries}
              onChange={(next) => setForm((current) => ({ ...current, country: next }))}
            />
          </label>
          <label className={fieldClass("ID number")}>
            <span>ID number</span>
            <input value={form.idNumber} onChange={setField("idNumber")} inputMode="numeric" maxLength={13} aria-invalid={missingSet.has("ID number") && form.idNumber ? "true" : "false"} />
            {showMissing && form.idNumber && missing.includes("ID number") ? (
              <small className="applicant-intake-form__hint">Enter a valid 13-digit South African ID number.</small>
            ) : null}
          </label>
          <label className={fieldClass("Gender")}>
            <span>Gender</span>
            <LineSelect
              value={form.gender}
              placeholder="Select"
              options={GENDERS.map((item) => ({ value: item, label: item }))}
              onChange={(next) => setForm((current) => ({ ...current, gender: next }))}
            />
          </label>
          <label className={fieldClass("Highest qualification")}>
            <span>Highest qualification</span>
            <LineSelect
              value={form.qualificationLevel}
              placeholder="Select"
              options={QUALIFICATIONS.map((item) => ({ value: item, label: item }))}
              onChange={(next) => setForm((current) => ({ ...current, qualificationLevel: next }))}
            />
          </label>
          <label className={fieldClass("Employment")}>
            <span>Employment</span>
            <LineSelect
              value={form.employmentStatus}
              placeholder="Select"
              options={EMPLOYMENT.map((item) => ({ value: item, label: item }))}
              onChange={(next) => setForm((current) => ({ ...current, employmentStatus: next }))}
            />
          </label>
          <label className={fieldClass("Subject understanding")}>
            <span>Subject understanding</span>
            <LineSelect
              value={form.subjectUnderstanding}
              placeholder="Select"
              options={UNDERSTANDING.map((item) => ({ value: item, label: item }))}
              onChange={(next) => setForm((current) => ({ ...current, subjectUnderstanding: next }))}
            />
          </label>
        </div>
        <label className={fieldClass("Reason for applying")}>
          <span>Reason for applying</span>
          <LineSelect
            value={form.reason}
            placeholder="Select"
            options={REASONS.map((item) => ({ value: item, label: item }))}
            onChange={(next) => setForm((current) => ({ ...current, reason: next }))}
          />
        </label>

        {showMissing && missing.length ? (
          <p className="applicant-intake-form__missing">Still needed: {missing.join(", ")}</p>
        ) : null}
        <button className="pa-primary-button" type="submit" disabled={busy || !email}>
          <span>{busy ? "Submitting…" : "Submit application"}</span>
          <span aria-hidden="true">›</span>
        </button>
        </>
        )}
        </div>
      </form>
    </AccessShell>
  );
}
